import { BUBBLE_POSITION_STORAGE_KEY } from "PFApp/components/propilot/constants";
import { useDockAndResetPosition } from "PFApp/components/propilot/hooks/use_dock_and_reset_position";
import { useIsProPilotEnabled } from "PFApp/components/propilot/hooks/use_is_propilot_enabled";
import { useIsBubbleOverProPilotDock, useProPilotDockActions } from "PFApp/stores/propilot_dock_store";
import { useProPilotStatus } from "PFApp/stores/propilot_lifecycle_store";
import {
  useFloatingEntry,
  useFloatingPositionStoreActions
} from "PFComponents/floating_expandable_bubble/floating_position_store";
import { isFocusLost } from "PFCore/helpers/is_focus_lost";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePrevious } from "react-use";

import { CommonNavItemProps, NavItem } from "../parts/nav_item/nav_item";
import css from "./propilot_nav_item.module.scss";

export const ProPilotNavItem = (props: CommonNavItemProps) => {
  const { t } = useTranslation("translation");
  const enabled = useIsProPilotEnabled();
  const status = useProPilotStatus();
  const { isOpen: isBoxOpen } = useFloatingEntry(BUBBLE_POSITION_STORAGE_KEY);
  const { setOpen } = useFloatingPositionStoreActions();
  const dockAndResetPosition = useDockAndResetPosition();
  const isBubbleOverDock = useIsBubbleOverProPilotDock();
  const buttonRef = useRef<HTMLElement>(null);

  const { registerElement, setBubbleOverDock } = useProPilotDockActions();
  const isFloatingElementVisible = status === "floating" || (status === "docked" && isBoxOpen);
  const wasFloatingElementVisible = usePrevious(isFloatingElementVisible);

  // Minimizing while docked, turning off or docking unmounts the focused element; refocus the navbar icon.
  useEffect(() => {
    if (wasFloatingElementVisible && !isFloatingElementVisible && isFocusLost()) {
      buttonRef.current?.focus();
    }
  }, [wasFloatingElementVisible, isFloatingElementVisible]);

  useEffect(() => {
    if (status !== "floating") {
      setBubbleOverDock(false);
    }
  }, [status, setBubbleOverDock]);

  if (!enabled) {
    return null;
  }

  // While floating, the item becomes the anchor the bubble can be dragged back onto.
  if (status === "floating") {
    return (
      <li ref={registerElement} className={css.dockSlot} aria-hidden="true" data-qa-id="ProPilotDockSlot">
        {isBubbleOverDock && <div className={css.dropTarget} data-qa-id="ProPilotDockDropTarget" />}
      </li>
    );
  }

  const handleClick = () => {
    if (status === "docked" && isBoxOpen) {
      setOpen(BUBBLE_POSITION_STORAGE_KEY, false);
      return;
    }
    dockAndResetPosition();
    setOpen(BUBBLE_POSITION_STORAGE_KEY, true);
  };

  return (
    <NavItem
      ref={buttonRef}
      id="propilot"
      icon="ai-agent"
      name={t("propilot.navbar.label")}
      onClick={handleClick}
      {...props}
    />
  );
};
